import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../config';
import { useAuth, useFeature, usePermission } from '../../context/AuthContext';
import EmploymentTab from './tabs/EmploymentTab';
import PersonalTab from './tabs/PersonalTab';
import DocumentsTab from './tabs/DocumentsTab';
import AccessTab from './tabs/AccessTab';
import ConnectedAppsTab from './connectedApps/ConnectedAppsTab';
import ActivityTab from './tabs/ActivityTab';
import AttentionStrip from './AttentionStrip';
import Avatar from './ui/Avatar';
import CardState from './ui/CardState';
import './profile.css';

/**
 * Old tab keys kept working after the six-to-four merge (the bar has since grown to six with Connected apps).
 *
 * No code in this repo deep-links a profile tab -- the department roster, the
 * org chart and the reporting line all link to the bare profile -- so this map
 * exists for users' bookmarks and pasted links, not for internal callers.
 */
export const TAB_ALIASES = {
  employment: 'overview',
  apps: 'access',
  tools: 'access',
  email: 'personal',
};

// Six tabs scroll sideways on a phone: keep the active one in view. Used as a
// ref on the active tab only, so it runs on mount and on every tab change.
// scrollIntoView is absent in jsdom.
const scrollTabIntoView = (el) => {
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ inline: 'nearest', block: 'nearest' });
};

export default function ProfilePage() {
  const { id } = useParams();
  const isMemberView = !!id;
  const buildAppEnabled = useFeature('build_your_app');
  const showOdoo = useFeature('odoo_profile_fields');
  const orgChartEnabled = useFeature('org_chart');
  const documentsEnabled = useFeature('employment_documents');
  const personalEnabled = useFeature('personal_details');
  const connectedAppsEnabled = useFeature('connected_apps');
  const itAppsEnabled = useFeature('it_apps');
  const canViewConnectedApps = usePermission('connected_apps.view');
  const canManageConnectedApps = usePermission('connected_apps.manage');
  const canOpenOrgChart = usePermission('org_chart.view');
  const canViewActivity = usePermission('profiles.activity');
  const canReviewApps = usePermission('apps.view');
  const viewerIsAdmin = !!useAuth()?.user?.is_admin;
  const [data, setData] = useState(null);
  const [pstate, setPstate] = useState('loading'); // loading|ok|forbidden|notfound|error
  const [searchParams, setSearchParams] = useSearchParams();
  const [selfTools, setSelfTools] = useState(null);
  // Bumped after a personal-details save or document upload so the attention
  // strip (mounted above the tabs, so never remounted) re-reads what is missing.
  const [attentionVersion, setAttentionVersion] = useState(0);
  const recheckAttention = () => setAttentionVersion((v) => v + 1);
  // The Personal form reports whether it holds unsaved edits. Switching tab
  // unmounts it, so without this a half-filled form vanished without a word.
  const personalDirty = useRef(false);
  const setPersonalDirty = useCallback((d) => { personalDirty.current = d; }, []);

  useEffect(() => {
    let active = true;
    setPstate('loading');
    const url = id ? `${API_BASE_URL}/api/profile/${id}` : `${API_BASE_URL}/api/profile`;
    (async () => {
      try {
        const r = await axios.get(url, { withCredentials: true });
        if (!active) return;
        setData(r.data); setPstate('ok');
      } catch (err) {
        if (!active) return;
        const code = err?.response?.status;
        setPstate(code === 403 ? 'forbidden' : code === 404 ? 'notfound' : 'error');
      }
    })();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    setSelfTools(null);
    if (isMemberView) return undefined;
    if (pstate !== 'ok') return undefined;
    let active = true;
    (async () => {
      try {
        // self tools: reuse the existing /api/my/tools endpoint (returns a flat
        // array of the signed-in user's tool rows, each with id + name).
        const r = await axios.get(`${API_BASE_URL}/api/my/tools`, { withCredentials: true });
        if (active) setSelfTools(Array.isArray(r.data) ? r.data : (r.data.tools || []));
      } catch {
        // Unknown, not empty: the Access tab says it couldn't load rather than
        // claiming the person has no tools.
        if (active) setSelfTools('error');
      }
    })();
    return () => { active = false; };
  }, [isMemberView, pstate]);

  if (pstate !== 'ok') {
    return (
      <div className="profile-page">
        <section className="profile-info-card">
          {pstate === 'loading' && <CardState kind="loading" label="Loading profile" lines={4} />}
          {pstate === 'forbidden' && <CardState kind="empty">You don&apos;t have access to this profile.</CardState>}
          {pstate === 'notfound' && <CardState kind="empty">User not found.</CardState>}
          {pstate === 'error' && <CardState kind="error" thing="this profile" />}
        </section>
      </div>
    );
  }

  // Tab visibility by viewer. Personal and Documents are self-only -- the
  // endpoints behind them have no per-id variant at all.
  const tabs = [
    { key: 'overview', label: 'Overview', show: true },
    { key: 'personal', label: 'Personal', show: personalEnabled && !isMemberView },
    { key: 'documents', label: 'Documents', show: documentsEnabled && !isMemberView },
    { key: 'access', label: 'Access', show: true },
    // The flag hides the feature; the tab does not also hide itself when
    // Workspace is unconfigured (it says so), which would cost a request per
    // profile load. Someone else's list needs connected_apps.view.
    { key: 'connected-apps', label: 'Connected apps', show: connectedAppsEnabled && (!isMemberView || canViewConnectedApps) },
    // Your own timeline always; someone else's with profiles.activity -- but
    // never an administrator's unless you are one (the server refuses it too).
    { key: 'activity', label: 'Activity', show: !isMemberView || (canViewActivity && (!data.is_admin || viewerIsAdmin)) },
  ].filter((t) => t.show);

  // Resolve ?tab= through the alias map, then confirm the result is actually
  // visible: an alias can point at a tab hidden on this view (?tab=email ->
  // personal, which a member view does not render), and landing there would
  // show an empty panel.
  const requested = searchParams.get('tab');
  const resolved = TAB_ALIASES[requested] || requested;
  const activeTab = tabs.some((t) => t.key === resolved) ? resolved : tabs[0].key;
  const setTab = (key) => {
    if (key === activeTab) return;
    if (activeTab === 'personal' && personalDirty.current
      && !window.confirm('Discard your unsaved changes?')) return;
    personalDirty.current = false;
    setSearchParams(key === tabs[0].key ? {} : { tab: key });
  };
  const odooEmployeeId = data?.odoo_employee_id ?? null;

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div className="profile-cover" aria-hidden="true">
          <span
            className="profile-cover__mark"
            style={{
              WebkitMaskImage: `url(${getImageUrl('/images/rtg-logo-red.png')})`,
              maskImage: `url(${getImageUrl('/images/rtg-logo-red.png')})`,
            }}
          />
        </div>
        <div className="profile-hero__bar">
          <Avatar name={data.name} src={data.photo_url} size="hero" className="profile-hero__avatar" />
          <div className="profile-hero__id">
            <h1 className="profile-hero__name">{data.name}</h1>
            <p className="profile-hero__role">{(showOdoo && data.odoo_job_title) || (data.is_admin ? 'Administrator' : 'Employee')}</p>
            {data.departments?.length > 0 && (
              <p className="profile-hero__where">
                {data.departments.map((d, i) => (
                  <span key={d.id}>
                    {i > 0 && ' · '}
                    {d.name}{d.is_manager && <span className="profile-hero__mgr"> (manager)</span>}
                  </span>
                ))}
              </p>
            )}
            {orgChartEnabled && canOpenOrgChart && odooEmployeeId && (
              <Link className="profile-hero__orgchart" to={`/org-chart?focus=emp-${odooEmployeeId}`}>
                View in org chart
              </Link>
            )}
          </div>
          <div className="profile-hero__contact">
            <span className="profile-hero__contact-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" /><path d="M3 7l9 6 9-6" />
              </svg>
              <a href={`mailto:${data.email}`} title={data.email}>{data.email}</a>
            </span>
            {showOdoo && data.odoo_mobile && (
              <span className="profile-hero__contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3-8.6A2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.6a2 2 0 01-.5 2.1L8.1 9.9a16 16 0 006 6l1.5-1.1a2 2 0 012.1-.5c.8.3 1.7.5 2.6.6a2 2 0 011.7 2z" />
                </svg>
                {/* Dialable, like the email beside it is clickable. Spaces
                    and punctuation are stripped from the href only -- the
                    displayed number keeps HR's formatting. */}
                <a href={`tel:${String(data.odoo_mobile).replace(/[^+\d]/g, '')}`}>{data.odoo_mobile}</a>
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Self view only: a colleague's outstanding paperwork is neither your
          business nor actionable by you. */}
      {!isMemberView && <AttentionStrip onGoToTab={setTab} refreshKey={attentionVersion} />}

      <nav className="profile-tabs" aria-label="Profile sections">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`profile-tab ${activeTab === t.key ? 'is-active' : ''}`}
            data-label={t.label}
            aria-current={activeTab === t.key ? 'page' : undefined}
            ref={activeTab === t.key ? scrollTabIntoView : undefined}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="profile-panel">
        {activeTab === 'overview' && <EmploymentTab data={data} showOdoo={showOdoo} isMemberView={isMemberView} />}
        {activeTab === 'personal' && !isMemberView && <PersonalTab data={data} onSaved={recheckAttention} onDirtyChange={setPersonalDirty} />}
        {activeTab === 'documents' && !isMemberView && <DocumentsTab onUploaded={recheckAttention} />}
        {activeTab === 'access' && (
          <AccessTab
            tools={isMemberView ? data.tools : selfTools}
            userId={id}
            isMemberView={isMemberView}
            buildAppEnabled={buildAppEnabled}
          />
        )}
        {activeTab === 'connected-apps' && (
          <ConnectedAppsTab
            key={id || 'self'}
            userId={id}
            isMemberView={isMemberView}
            canManage={isMemberView && canManageConnectedApps}
            itAppsEnabled={itAppsEnabled}
            subjectFirstName={isMemberView && data.name ? String(data.name).trim().split(/\s+/)[0] : ''}
          />
        )}
        {activeTab === 'activity' && (
          <ActivityTab
            userId={isMemberView ? id : undefined}
            subjectName={data.name}
            isSelf={!isMemberView}
            canReviewApps={canReviewApps}
          />
        )}
      </div>
    </div>
  );
}
